import { useSearchParams } from "react-router";
import { lastPageOf, writePageParam } from "@/utils";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { DataTableSource, TableRow } from "@carbon6/c6-frontend-design";

// 관리자 운영 목록의 생명주기를 관리한다. 목록을 표시하는 동안만 폴링한다.
// 조회가 진행 중이면 다음 폴링을 시작하지 않는다.
export function useAdminTable<Row extends TableRow>(source: DataTableSource<Row>, poll = true) {
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const [, setParams] = useSearchParams();
  const setPage = useCallback(
    (page: number) => setParams((prev) => writePageParam(prev, page), { replace: true }),
    [setParams],
  );
  useEffect(() => {
    const lastPage = lastPageOf(snapshot.total, snapshot.size);
    if (snapshot.loadState === "loaded" && snapshot.page > lastPage) setPage(lastPage);
  }, [snapshot.loadState, snapshot.page, snapshot.total, snapshot.size, setPage]);
  useEffect(() => {
    void source.initialize();
    const refresh = () => {
      if (document.visibilityState !== "hidden" && source.getSnapshot().loadState !== "loading") {
        void source.refresh();
      }
    };
    const timer = poll ? setInterval(refresh, 5_000) : null;
    if (poll) document.addEventListener("visibilitychange", refresh);
    return () => {
      if (timer !== null) clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      source.dispose();
    };
  }, [source, poll]);
  return { ...snapshot, setPage };
}
